import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Container,
  Typography,
  Snackbar,
  Alert,
  Avatar,
  Paper,
  Stack,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../config/axios";
import Swal from "sweetalert2";

const ChessGame = () => {
  const game = useMemo(() => new Chess(), []);
  const [gamePosition, setGamePosition] = useState(game.fen());
  const [gameStatus, setGameStatus] = useState("");
  const [playVsComputer, setPlayVsComputer] = useState(true);
  const [isComputerTurn, setIsComputerTurn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [highlightSquares, setHighlightSquares] = useState({});
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);
  const [gameMode, setGameMode] = useState(null); // "ai" or "random"
  const [opponent, setOpponent] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [gameId, setGameId] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Initialize user and show game mode selection dialog
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      Swal.fire({
        title: "Login Required",
        text: "Please login to play chess",
        icon: "warning",
        confirmButtonText: "Login",
      }).then(() => {
        navigate("/login", { state: { returnUrl: location.pathname } });
      });
      return;
    }

    setCurrentUser(user);

    // Show game mode selection dialog if coming from sidebar
    if (location.state?.fromSidebar) {
      showGameModeDialog();
    } else {
      // Default to AI mode if just visiting the page directly
      setGameMode("ai");
      setPlayVsComputer(true);
    }
  }, [location, navigate]);

  // Show dialog to select game mode
  const showGameModeDialog = () => {
    Swal.fire({
      title: "Choose Game Mode",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Play with AI",
      cancelButtonText: "Play with Random Player",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Play with AI
        setGameMode("ai");
        setPlayVsComputer(true);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // Play with random player
        setGameMode("random");
        setPlayVsComputer(false);
        findRandomOpponent();
      }
    });
  };

  // Find a random opponent
  const findRandomOpponent = async () => {
    try {
      setIsWaitingForOpponent(true);

      // Show waiting message
      Swal.fire({
        title: "Finding Opponent",
        text: "Searching for a random player...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Call API to find a random opponent
      const response = await api.post("/chess/find-opponent", {
        userId: currentUser._id,
      });

      if (response.data.status === "waiting") {
        // No opponent found yet, poll for opponent
        pollForOpponent();
      } else if (response.data.status === "matched") {
        // Opponent found immediately
        Swal.close();
        setIsWaitingForOpponent(false);
        setOpponent(response.data.opponent);
        setGameId(response.data.gameId);
      }
    } catch (error) {
      console.error("Error finding opponent:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to find an opponent. Please try again.",
        icon: "error",
      });
      setIsWaitingForOpponent(false);
      setGameMode("ai");
      setPlayVsComputer(true);
    }
  };

  // Poll for opponent match
  const pollForOpponent = useCallback(async () => {
    try {
      const checkInterval = setInterval(async () => {
        const response = await api.get(`/chess/check-match/${currentUser._id}`);

        if (response.data.status === "matched") {
          clearInterval(checkInterval);
          Swal.close();
          setIsWaitingForOpponent(false);
          setOpponent(response.data.opponent);
          setGameId(response.data.gameId);

          // Show match found alert
          Swal.fire({
            title: "Opponent Found!",
            text: `You are playing against ${response.data.opponent.firstName} ${response.data.opponent.lastName}`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }, 3000);

      // Clean up interval on component unmount
      return () => clearInterval(checkInterval);
    } catch (error) {
      console.error("Error polling for opponent:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to connect with opponent. Playing with AI instead.",
        icon: "error",
      });
      setIsWaitingForOpponent(false);
      setGameMode("ai");
      setPlayVsComputer(true);
    }
  }, [currentUser]);

  useEffect(() => {
    let pollInterval;

    if (isWaitingForOpponent && currentUser) {
      pollInterval = pollForOpponent();
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isWaitingForOpponent, currentUser, pollForOpponent]);

  // Make a random move for the computer
  const makeComputerMove = () => {
    if (!playVsComputer || !isComputerTurn || game.isGameOver()) return;

    try {
      // Get all possible moves
      const possibleMoves = game.moves({ verbose: true });

      if (possibleMoves.length > 0) {
        // Select a random move
        const randomIndex = Math.floor(Math.random() * possibleMoves.length);
        const randomMove = possibleMoves[randomIndex];

        // Make the move
        game.move({
          from: randomMove.from,
          to: randomMove.to,
          promotion: randomMove.promotion || "q",
        });
        setGamePosition(game.fen());

        // Check game status after move
        updateGameStatus();
        setIsComputerTurn(false);
      }
    } catch (error) {
      console.log("Computer move error:", error);
      showErrorAlert(
        "Computer tried an invalid move. Please restart the game."
      );
      setIsComputerTurn(false);
    }
  };

  // Effect to handle computer's turn
  useEffect(() => {
    if (isComputerTurn) {
      // Add a small delay to make it seem like the computer is "thinking"
      const timeoutId = setTimeout(() => {
        makeComputerMove();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isComputerTurn]);

  // Handle online game moves
  useEffect(() => {
    if (gameMode === "random" && gameId && !playVsComputer) {
      // Set up listener for opponent moves
      const moveCheckInterval = setInterval(async () => {
        try {
          const response = await api.get(`/chess/get-game/${gameId}`);
          if (
            response.data.lastMove &&
            response.data.currentTurn === currentUser._id
          ) {
            // Apply opponent's move
            game.move(response.data.lastMove);
            setGamePosition(game.fen());
            updateGameStatus();
          }
        } catch (error) {
          console.error("Error checking for moves:", error);
        }
      }, 2000);

      return () => clearInterval(moveCheckInterval);
    }
  }, [gameMode, gameId, playVsComputer, currentUser]);

  const showErrorAlert = (message) => {
    setErrorMessage(message);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const updateGameStatus = () => {
    if (game.isGameOver()) {
      if (game.isCheckmate()) {
        setGameStatus(
          `Checkmate! ${game.turn() === "w" ? "Black" : "White"} wins!`
        );
      } else if (game.isDraw()) {
        setGameStatus("Game ended in a draw!");
      }
    } else if (game.isCheck()) {
      setGameStatus("Check!");
    } else {
      setGameStatus("");
    }
  };

  const highlightPossibleMoves = (square) => {
    // Get all valid moves for the selected piece
    const moves = game.moves({
      square,
      verbose: true,
    });

    // Create an object with highlighted squares
    const newSquares = {};

    // Add highlights for possible moves
    moves.forEach((move) => {
      newSquares[move.to] = {
        background: move.captured
          ? "radial-gradient(circle, rgba(220, 0, 0, 0.8) 17%, transparent 20%)" // Red dot for captures
          : "radial-gradient(circle, rgba(0, 121, 255, 0.8) 17%, transparent 20%)", // Blue dot for moves
        position: "relative",
        zIndex: 0,
      };
    });

    // Also highlight the selected square
    newSquares[square] = {
      background: "rgba(255, 215, 0, 0.4)", // Gold for selected piece
      position: "relative",
      zIndex: 0,
    };

    setHighlightSquares(newSquares);
  };

  // Handle piece drag start
  const onPieceDragBegin = (piece, square) => {
    // Only allow the current player to move their pieces
    const pieceObj = game.get(square);
    if (
      !pieceObj ||
      pieceObj.color !== game.turn() ||
      (playVsComputer && isComputerTurn) ||
      game.isGameOver()
    ) {
      return false;
    }

    // In online mode, only allow moves on your turn
    if (gameMode === "random" && opponent) {
      const isWhiteTurn = game.turn() === "w";
      // For simplicity, current user is always white in this implementation
      if (
        (isWhiteTurn && currentUser._id !== currentUser._id) ||
        (!isWhiteTurn && currentUser._id === currentUser._id)
      ) {
        showErrorAlert("It's not your turn!");
        return false;
      }
    }

    // Show possible moves
    highlightPossibleMoves(square);
    return true;
  };

  // Handle piece drop
  const onPieceDrop = async (sourceSquare, targetSquare, piece) => {
    // If the game is over or it's computer's turn, don't allow moves
    if (game.isGameOver() || (playVsComputer && isComputerTurn)) {
      return false;
    }

    try {
      // Try to make a move
      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: "q", // Always promote to queen for simplicity
      });

      // If move is valid
      if (move) {
        setGamePosition(game.fen());
        updateGameStatus();
        setHighlightSquares({});

        // If playing online, send move to server
        if (gameMode === "random" && gameId) {
          try {
            await api.post(`/chess/make-move`, {
              gameId,
              move: {
                from: sourceSquare,
                to: targetSquare,
                promotion: "q",
              },
              userId: currentUser._id,
            });
          } catch (error) {
            console.error("Error sending move to server:", error);
            showErrorAlert("Failed to send move to opponent");
          }
        }

        // Set computer's turn if playing against computer
        if (playVsComputer && !game.isGameOver()) {
          setIsComputerTurn(true);
        }
        return true;
      } else {
        // If the move is invalid, show an error
        showErrorAlert("Invalid move! Please select a valid destination.");
        return false;
      }
    } catch (error) {
      console.log("Move error:", error);
      showErrorAlert("Invalid move! Please select a valid destination.");
      setHighlightSquares({});
      return false;
    }
  };

  // Handle square click for highlighting possible moves
  const onSquareClick = (square) => {
    // If the game is over or it's computer's turn, don't allow interaction
    if (game.isGameOver() || (playVsComputer && isComputerTurn)) {
      return;
    }

    try {
      // Check if there's a piece on the clicked square
      const piece = game.get(square);

      // If clicking on a piece of the current player's color, highlight moves
      if (piece && piece.color === game.turn()) {
        highlightPossibleMoves(square);
      } else {
        // Clear highlights if clicking elsewhere
        setHighlightSquares({});
      }
    } catch (error) {
      console.log("Square click error:", error);
    }
  };

  const handleNewGame = () => {
    game.reset();
    setGamePosition(game.fen());
    setGameStatus("");
    setIsComputerTurn(false);
    setHighlightSquares({});

    // If in random mode, ask if user wants to find a new opponent
    if (gameMode === "random") {
      Swal.fire({
        title: "New Game",
        text: "Do you want to find a new opponent?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, find new opponent",
        cancelButtonText: "No, play with AI",
      }).then((result) => {
        if (result.isConfirmed) {
          setOpponent(null);
          setGameId(null);
          findRandomOpponent();
        } else {
          setGameMode("ai");
          setPlayVsComputer(true);
        }
      });
    }
  };

  const handleUndo = () => {
    try {
      if (playVsComputer) {
        // Undo both player's and computer's moves
        if (game.history().length >= 2) {
          game.undo();
          game.undo();
          setGamePosition(game.fen());
          setGameStatus("");
          setIsComputerTurn(false);
        } else if (game.history().length === 1) {
          game.undo();
          setGamePosition(game.fen());
          setGameStatus("");
          setIsComputerTurn(false);
        }
      } else {
        // In random mode, don't allow undo
        if (gameMode === "random") {
          showErrorAlert("Undo is not available in multiplayer mode");
          return;
        }

        // Just undo the last move
        if (game.history().length > 0) {
          game.undo();
          setGamePosition(game.fen());
          setGameStatus("");
        }
      }
      setHighlightSquares({});
    } catch (error) {
      console.log("Undo error:", error);
      showErrorAlert("Could not undo the move. Please try again.");
    }
  };

  const togglePlayMode = () => {
    // Only allow toggling if not in random mode
    if (gameMode === "random") {
      Swal.fire({
        title: "Cannot Switch Mode",
        text: "You're currently in an online game. Start a new game to switch modes.",
        icon: "warning",
      });
      return;
    }

    setPlayVsComputer(!playVsComputer);
    handleNewGame();
  };

  // Render player profile info
  const renderPlayerInfo = () => {
    if (gameMode !== "random" || !opponent) return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", mb: 2, gap: 2 }}>
        {/* Current User Info */}
        <Paper
          elevation={1}
          sx={{ p: 2, display: "flex", alignItems: "center", borderRadius: 2 }}
        >
          <Avatar
            src={currentUser?.profileImage || "/images/default-avatar.jpeg"}
            alt={currentUser?.firstName}
            sx={{ width: 40, height: 40, mr: 2 }}
          />
          <Typography variant="subtitle1" fontWeight="bold">
            {currentUser?.firstName} {currentUser?.lastName} (You)
          </Typography>
        </Paper>

        {/* Opponent Info */}
        <Paper
          elevation={1}
          sx={{ p: 2, display: "flex", alignItems: "center", borderRadius: 2 }}
        >
          <Avatar
            src={opponent?.profileImage || "/images/default-avatar.jpeg"}
            alt={opponent?.firstName}
            sx={{ width: 40, height: 40, mr: 2 }}
          />
          <Typography variant="subtitle1" fontWeight="bold">
            {opponent?.firstName} {opponent?.lastName} (Opponent)
          </Typography>
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Chess Game
      </Typography>

      {gameMode === "ai" && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={playVsComputer}
                onChange={togglePlayMode}
                color="primary"
              />
            }
            label={playVsComputer ? "Playing vs Computer" : "Two Player Mode"}
          />
        </Box>
      )}

      {gameStatus && (
        <Typography
          variant="h6"
          align="center"
          sx={{
            mb: 2,
            color: gameStatus.includes("Checkmate")
              ? "error.main"
              : "primary.main",
          }}
        >
          {gameStatus}
        </Typography>
      )}

      {renderPlayerInfo()}

      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Chessboard
          id="ChessGame"
          position={gamePosition}
          onPieceDrop={onPieceDrop}
          onPieceDragBegin={onPieceDragBegin}
          onSquareClick={onSquareClick}
          boardWidth={600}
          customSquareStyles={highlightSquares}
          areArrowsAllowed={false}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleNewGame}
          sx={{ bgcolor: "primary.main" }}
        >
          NEW GAME
        </Button>
        <Button
          variant="contained"
          onClick={handleUndo}
          sx={{ bgcolor: "secondary.main" }}
          disabled={game.history().length === 0 || gameMode === "random"}
        >
          UNDO
        </Button>
      </Box>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChessGame;
